import React, { useState } from 'react';
import Container from '@mui/material/Container';
import { Button, TextField } from '@mui/material';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Recaptcha, { getRecaptchaToken } from "../../shared/Recaptcha";
import CircularProgress from '@mui/material/CircularProgress';
import Checkbox from '@mui/material/Checkbox';
import { Link } from 'react-router-dom';
import { registerUser } from "../../api/Api";
import { getAttribution } from '../../utils/attribution';
import GlowCard from '../../shared/GlowCard';
import { useColors } from '../../theme/ColorModeContext';
import AuthBrandHeader from '../../shared/AuthBrandHeader';
import FormHelperText from '@mui/material/FormHelperText';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
    PASSWORD_HINT,
    RegistrationErrors,
    RegistrationField,
    RegistrationValues,
    USERNAME_HINT,
    validateRegistration,
} from './validation';

type Status = { kind: 'error' | 'success'; text: string };

const Register = () => {
    const colors = useColors();
    const [errors, setErrors] = useState<RegistrationErrors>({});
    const [status, setStatus] = useState<Status | null>(null);
    const [processing, setProcessing] = useState(false);

    // Drop a field's error as soon as the user edits it, so a stale message
    // never sits next to a value that is now fine.
    const handleChange = (e: React.FormEvent<HTMLFormElement>) => {
        const name = (e.target as HTMLInputElement).name as RegistrationField;
        if (errors[name]) {
            setErrors(({ [name]: _, ...rest }) => rest);
        }
    };

    const handleSubmit = async(e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const data = new FormData(e.currentTarget);
        const values: RegistrationValues = {
            username: `${data.get("username") ?? ""}`.trim(),
            email: `${data.get("email") ?? ""}`.trim(),
            password: `${data.get("password") ?? ""}`,
            confirmPassword: `${data.get("confirmPassword") ?? ""}`,
            acceptTerms: data.get("acceptTerms") !== null,
        };

        const validationErrors = validateRegistration(values);
        setErrors(validationErrors);
        setStatus(null);
        const firstInvalid = Object.keys(validationErrors)[0];
        if (firstInvalid) {
            document.getElementById(firstInvalid)?.focus();
            return;
        }

        // Never a blocker: registration must not be conditional on marketing
        // consent, so an unticked box just means no.
        const marketing_consent = data.get("marketingConsent") !== null;

        setProcessing(true);
        let recaptcha_token: string;
        try {
            recaptcha_token = await getRecaptchaToken();
        } catch {
            setProcessing(false);
            setStatus({ kind: 'error', text: "Could not load reCAPTCHA. Check your connection or content blocker and try again" });
            return;
        }
        const response = await registerUser({
            username: values.username,
            email: values.email,
            password: values.password,
            confirm_password: values.confirmPassword,
            recaptcha_token,
            accept_terms: values.acceptTerms,
            marketing_consent,
            attribution: getAttribution(),
        });
        setProcessing(false);

        if (response?.status === 200) {
            setStatus({ kind: 'success', text: `We sent an activation link to ${values.email}. Open it to activate your account, then sign in.` });
        } else if (!response) {
            // registerUser resolves to undefined when no response arrived at all.
            setStatus({ kind: 'error', text: "Could not reach the server. Check your connection and try again" });
        } else {
            // A proxy error page or an unhandled exception has no msg field.
            const msg = response.data?.msg;
            setStatus({ kind: 'error', text: typeof msg === "string" && msg ? msg : "Something went wrong on our side. Please try again in a moment" });
        }
    }

    return (
        <Container sx={{ marginTop: '120px', marginBottom: '60px' }} disableGutters maxWidth="sm" className="page-enter">
            <AuthBrandHeader />
            <GlowCard gradientBorder sx={{ padding: '48px 32px' }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}>
                    <Typography variant="h4" sx={{
                        fontFamily: '"Orbitron", sans-serif',
                        fontWeight: 700,
                        background: colors.gradient.primary,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: '32px',
                    }}>
                        Create Account
                    </Typography>
                    {status?.kind === 'success' ? (
                        <Box role="status" sx={{
                            color: colors.accent.green,
                            padding: '16px',
                            backgroundColor: colors.accent.greenGlow,
                            borderRadius: '8px',
                            textAlign: 'center',
                            width: '100%',
                        }}>
                            <Typography variant="h6" sx={{ marginBottom: '8px' }}>Check your inbox</Typography>
                            <Typography variant="body2">{status.text}</Typography>
                        </Box>
                    ) : (
                        <Box component="form" onSubmit={handleSubmit} onChange={handleChange} noValidate sx={{ width: '100%' }}>
                            <TextField margin="normal" required fullWidth id="username" label="Username" name="username" autoComplete="username" autoFocus
                                error={!!errors.username} helperText={errors.username ?? USERNAME_HINT} />
                            <TextField margin="normal" required fullWidth id="email" label="Email" name="email" type="email" autoComplete="email"
                                error={!!errors.email} helperText={errors.email} />
                            <TextField margin="normal" required fullWidth name="password" id="password" label="Password" type="password" autoComplete="new-password"
                                error={!!errors.password} helperText={errors.password ?? PASSWORD_HINT} />
                            <TextField margin="normal" required fullWidth name="confirmPassword" id="confirmPassword" label="Confirm Password" type="password" autoComplete="new-password"
                                error={!!errors.confirmPassword} helperText={errors.confirmPassword} />

                            {/*
                              * Its own unticked box, placed apart from the Terms checkbox
                              * and styled as an offer rather than fine print. Consent to
                              * marketing has to be a distinct, affirmative act: it cannot
                              * ride along with accepting the Terms, and it is never
                              * required to register. Changing this wording means bumping
                              * MARKETING_CONSENT_VERSION in the API config.
                              */}
                            <FormControlLabel
                                control={<Checkbox name="marketingConsent" id="marketingConsent" sx={{ marginTop: '-8px' }} />}
                                label={
                                    <>
                                        <Typography variant="body2" sx={{ color: colors.text.primary, fontWeight: 600 }}>
                                            Get new challenges the day they drop
                                        </Typography>
                                        <Typography variant="body2" sx={{ color: colors.text.secondary, marginTop: '2px' }}>
                                            An email when new challenges and chain tracks go live, plus the occasional
                                            platform update. A couple of times a month at most. Unsubscribe from any
                                            email or in Settings.
                                        </Typography>
                                    </>
                                }
                                sx={{
                                    alignItems: 'flex-start',
                                    margin: '16px 0 0',
                                    padding: '12px 12px 12px 4px',
                                    width: '100%',
                                    border: `1px solid ${colors.border.default}`,
                                    borderRadius: '8px',
                                    backgroundColor: colors.bg.secondary,
                                    transition: 'border-color 0.2s',
                                    '&:hover, &:has(input:checked)': { borderColor: colors.border.accent },
                                }}
                            />

                            <FormControlLabel
                                control={
                                    <Checkbox name="acceptTerms" id="acceptTerms"
                                        inputProps={{ 'aria-invalid': !!errors.acceptTerms, 'aria-describedby': errors.acceptTerms ? 'acceptTerms-error' : undefined }}
                                        sx={errors.acceptTerms ? { color: colors.accent.red } : undefined} />
                                }
                                label={
                                    <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                                        I accept{' '}
                                        <Link to={"/terms-and-conditions"} style={{ color: colors.accent.cyan, textDecoration: 'none' }}>
                                            Terms and Conditions
                                        </Link>{' '}and{' '}
                                        <Link to={"/privacy-policy"} style={{ color: colors.accent.cyan, textDecoration: 'none' }}>
                                            Privacy Policy
                                        </Link>
                                    </Typography>
                                }
                                sx={{ margin: '12px 0 0' }}
                            />
                            {errors.acceptTerms && (
                                <FormHelperText error id="acceptTerms-error" sx={{ marginLeft: '14px' }}>
                                    {errors.acceptTerms}
                                </FormHelperText>
                            )}

                            <Recaptcha />

                            {status?.kind === 'error' && (
                                <Typography role="alert" variant="body2" sx={{
                                    color: colors.accent.red,
                                    marginTop: '16px',
                                    padding: '8px 12px',
                                    backgroundColor: colors.accent.redGlow,
                                    borderRadius: '8px',
                                    textAlign: 'center',
                                }}>
                                    {status.text}
                                </Typography>
                            )}

                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                disabled={processing}
                                sx={{ mt: 3, mb: 2, padding: '12px' }}
                            >
                                Register
                                {processing && <CircularProgress size={20} sx={{ marginLeft: '10px', color: colors.bg.primary }} />}
                            </Button>
                        </Box>
                    )}
                </Box>
            </GlowCard>
            <Box sx={{ textAlign: 'center', marginTop: '16px' }}>
                <Typography variant="body2" sx={{ color: colors.text.secondary }}>
                    Already have an account?{' '}
                    <Link to={"/login"} style={{ color: colors.accent.cyan, fontWeight: 600, textDecoration: 'none' }}>
                        Sign In
                    </Link>
                </Typography>
            </Box>
        </Container>
    );
}

export default Register;
